import { useQuery } from '@apollo/client';
import { COMMENTS, useFetchItems } from 'pe-core-frontend';
import { useMemo } from 'react';
export const useComments = ({ commentableId, commentableType, isInternal, skip, }) => {
    const { items, loading, error, refetch } = useFetchItems({
        fetchQuery: COMMENTS,
        spec: { commentableId, commentableType, isInternal },
        dataProp: 'comments',
        skip,
    });
    return { comments: items || [], loading, error, refetch };
};
/**
 * Hook for fetching comments from both program and payment request for single expense flow.
 * Makes two explicit queries and merges the results.
 */
export const useCommentsSingleExpense = ({ programId, paymentRequestId, isInternal, skip, }) => {
    // Query 1: Program comments
    const programQuery = useQuery(COMMENTS, {
        variables: {
            spec: {
                commentableId: programId,
                commentableType: 'programs',
                isInternal,
            },
        },
        skip,
    });
    // Query 2: Payment request comments
    const paymentRequestQuery = useQuery(COMMENTS, {
        variables: {
            spec: {
                commentableId: paymentRequestId,
                commentableType: 'payment_requests',
                isInternal,
            },
        },
        skip,
    });
    // Merge and sort comments by createdAt (oldest first)
    const comments = useMemo(() => {
        const programComments = programQuery.data?.comments || [];
        const paymentRequestComments = paymentRequestQuery.data?.comments || [];
        const allComments = [...programComments, ...paymentRequestComments];
        return allComments.sort((a, b) => {
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return aTime - bTime;
        });
    }, [programQuery.data?.comments, paymentRequestQuery.data?.comments]);
    // Combine loading states - loading if either query is loading
    const loading = programQuery.loading || paymentRequestQuery.loading;
    // Combine errors - return first error if any exist
    const error = programQuery.error || paymentRequestQuery.error;
    // Refetch both queries
    const refetch = async () => {
        await Promise.all([programQuery.refetch(), paymentRequestQuery.refetch()]);
    };
    return { comments, loading, error, refetch };
};
